/**
 * The metric engine.
 *
 * Pure and side-effect free: it takes a portfolio snapshot and returns raw
 * numbers only — no currency symbols, no locale, no translated labels. The
 * server exposes the result on /api/metrics; the clients run the very same
 * function offline. Both therefore always agree on the arithmetic, and only
 * disagree (deliberately) on presentation.
 */

import {
  CATEGORY_COLORS, CAR_TYPE_COLORS, CLASS_PALETTE, shiftMonth,
  economyFrom, economyHigherIsBetter, kmToMiles, litresToGallons,
  DEFAULT_BASE_CURRENCY, toBase, resolveRates, priceForMonth, isMonth, isDate, isAmountOnly,
  debtBalanceAt, debtHistory, metalUnitFactor
} from './model.js';
import { PROP_COSTS, PROP_RETURNS, currentMonth, localDay } from './model.js';

const num = v => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const sum = (list, key) => list.reduce((acc, row) => acc + num(row[key]), 0);

/** Whole months from one 'YYYY-MM' to another; negative when `to` is earlier. */
function monthsBetween(from, to) {
  if (!isMonth(from) || !isMonth(to)) return 0;
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm);
}

/**
 * The instalment that clears a balance over a stated term.
 *
 * The inverse of {@link amortise}: P = rB / (1 - (1+r)^-n). Used when somebody
 * described their loan the way the agreement does — a rate and a number of
 * instalments — rather than by the payment, which is the figure people are
 * least likely to have to hand and most likely to round.
 */
export function paymentFor(balance, annualRatePct, months) {
  const B = num(balance);
  const n = num(months);
  const r = num(annualRatePct) / 100 / 12;

  if (B <= 0 || n <= 0) return 0;
  if (r <= 0) return B / n;
  return (r * B) / (1 - Math.pow(1 + r, -n));
}

/**
 * How long a debt actually takes to clear, and what the interest costs.
 *
 * The previous answer was `ceil(balance / payment)`, which is the arithmetic
 * for an interest-free loan. Every debt in this app carries an APR — it is a
 * field on the form, it is shown on the row, and it was used for a "monthly
 * interest" figure — and none of it reached the payoff estimate. A 20 000 zl
 * balance at 18% paid at 600 a month came out as 34 months; it is 46. The app
 * was telling people they would be debt-free a year before they will be.
 *
 * Standard amortisation: with monthly rate r, balance B and payment P,
 *
 *   n = -ln(1 - rB/P) / ln(1 + r)
 *
 * The edge that matters is P <= rB: the payment does not cover the interest,
 * the balance grows, and there is no n. That is not a rounding problem to paper
 * over — it is the single most important thing a person in that position can be
 * told, so it comes back as its own flag rather than as a big number.
 */
export function amortise(balance, annualRatePct, payment) {
  const B = num(balance);
  const P = num(payment);
  const r = num(annualRatePct) / 100 / 12;

  if (B <= 0) return { months: 0, interest: 0, coversInterest: true };
  if (P <= 0) return { months: null, interest: 0, coversInterest: false };

  // No interest: the simple division is the right answer here, and only here.
  if (r <= 0) {
    const months = Math.ceil(B / P);
    return { months, interest: 0, coversInterest: true };
  }

  const monthlyInterest = B * r;
  if (P <= monthlyInterest) return { months: null, interest: 0, coversInterest: false };

  const months = Math.ceil(-Math.log(1 - (r * B) / P) / Math.log(1 + r));
  /*
   * The final payment is partial, so n*P overstates what is actually paid.
   * Walking the balance down gives the real figure and costs nothing at these
   * sizes; the closed form above is only used to know when to stop.
   */
  let remaining = B;
  let interest = 0;
  for (let month = 0; month < months && remaining > 0; month++) {
    const charge = remaining * r;
    interest += charge;
    remaining = remaining + charge - P;
  }
  return { months, interest: Math.max(0, interest), coversInterest: true };
}


/**
 * @param {object} data    portfolio snapshot
 * @param {object} options { month: 'YYYY-MM', strategy: 'rate' | 'balance' }
 */
/** How many months the trend shows, filling the panel whatever is recorded. */
const TREND_MONTHS = 12;

/*
 * How far past today the trend reaches.
 *
 * Enough to see the next quarter's standing payments land, and few enough that
 * most of the chart is still measurement rather than projection.
 */
const TREND_AHEAD = 3;

export function computeMetrics(data, options = {}) {
  const month = options.month || currentMonth();
  /*
   * The calendar month, as distinct from the month being viewed.
   *
   * Only used to tell a reading from a projection: everything up to here can be
   * observed, everything after it can only be carried forward. Overridable so a
   * test can pin "now" instead of drifting into failure next month.
   */
  const thisMonth = options.now || currentMonth();
  const strategy = options.strategy === 'balance' ? 'balance' : 'rate';
  // Storage is metric; this only decides how the vehicle figures are reported.
  const unitSystem = options.unitSystem === 'imperial' ? 'imperial' : 'metric';
  const toDistance = km => (unitSystem === 'imperial' ? kmToMiles(km) : km);
  const toVolume = litres => (unitSystem === 'imperial' ? litresToGallons(litres) : litres);

  const base = options.baseCurrency || data.settings?.baseCurrency || DEFAULT_BASE_CURRENCY;
  /*
   * Resolved for the month being computed, not for today. Looking at March has
   * to show March: re-valuing a closed month at the current rate would invent a
   * gain or a loss that nothing in the ledger ever recorded.
   */
  const ratesCache = new Map();
  const ratesAt = target => {
    if (!ratesCache.has(target)) {
      ratesCache.set(
        target,
        data.rateHistory ? resolveRates(data.rateHistory, target) : (data.rates || {})
      );
    }
    return ratesCache.get(target);
  };
  const rates = ratesAt(month);

  /*
   * Amounts are stored in the currency they were entered in. Everything that
   * gets summed has to pass through here first, and a row whose rate is missing
   * is *left out* rather than counted at par — a total that quietly treats a
   * dollar as a złoty is worse than a total that admits it is incomplete.
   */
  const missingRates = new Set();
  const convert = row => {
    const value = toBase(num(row.amount ?? row.value ?? 0), row.currency, base, rates);
    if (value === null) missingRates.add(String(row.currency || '').toUpperCase());
    return value;
  };
  const convertAmount = (amount, currency) => {
    const value = toBase(num(amount), currency, base, rates);
    if (value === null) missingRates.add(String(currency || '').toUpperCase());
    return value;
  };
  /** Sums what can be converted; unconvertible rows are reported, not guessed. */
  const sumBase = (list, key = 'amount') => list.reduce((total, row) => {
    const value = convertAmount(row[key], row.currency);
    return total + (value === null ? 0 : value);
  }, 0);

  const cashBalances = Array.isArray(data.cash)
    ? data.cash
    : (num(data.cash) ? [{ currency: base, amount: num(data.cash) }] : []);
  const cash = sumBase(cashBalances);

  const income = data.income || [];
  const expenses = data.expenses || [];
  const allDebts = data.debts || [];
  /*
   * The debts on the books in the month on screen, at that month's balance.
   *
   * A debt used to be one figure, today's, shown the same in every month, so
   * going back to August said what was owed in September. Each is read from
   * its own history now (see debtBalanceAt): not there before it was taken,
   * and gone again the month after the one that paid it off -- that month
   * still shows it, at zero, because that is the month it went. A debt that
   * was never above zero stays visible, or there would be no way to delete it.
   */
  /*
   * A month after today has no balance of its own yet, so it is projected:
   * from the latest balance known by then -- a payment already filed for a
   * future month, or today's -- each month accrues its interest and takes its
   * instalment. It used to repeat that last balance, so once November had a
   * record December and January read "unchanged" while the chart above them
   * went on falling. A debt with no instalment grows by its interest, which is
   * what it would do. Past months, and this one, keep what was recorded.
   */
  const balanceAt = (debt, target) => {
    const recorded = debtBalanceAt(debt, target);
    if (recorded === null || target <= thisMonth) return recorded;
    const filed = debtHistory(debt).filter(row => row.month <= target).pop();
    const from = filed && filed.month > thisMonth ? filed.month : thisMonth;
    const start = from === thisMonth ? debtBalanceAt(debt, thisMonth) : filed.balance;
    const steps = monthsBetween(from, target);
    if (start === null || steps <= 0) return recorded;
    const rate = num(debt.rate) / 1200;
    const term = Number.isFinite(debt.term) && debt.term > 0 ? Math.round(debt.term) : null;
    const payment = num(debt.payment) > 0 ? num(debt.payment) : paymentFor(start, num(debt.rate), term);
    /*
     * A card, or a loan with no instalment to go by, stays where it was: what
     * will be spent or paid on it is not known, and letting it grow by its
     * interest invented a figure nobody would see on a statement. Its balance
     * moves when a payment or a correction says so.
     */
    if (debt.kind === 'card' || !(payment > 0)) return start;
    let balance = start;
    for (let i = 0; i < steps && balance > 0.005; i += 1) balance = Math.max(0, balance * (1 + rate) - payment);
    return Math.round(balance * 100) / 100;
  };

  const monthBefore = shiftMonth(month, -1);
  const debts = allDebts
    .map(debt => ({
      ...debt,
      balance: balanceAt(debt, month),
      prevBalance: balanceAt(debt, monthBefore)
    }))
    .filter(debt => {
      if (debt.balance === null) return false;
      if (debt.balance > 0 || (debt.prevBalance || 0) > 0) return true;
      return !debtHistory(debt).some(row => row.month < month && row.balance > 0);
    });
  const allHoldings = data.holdings || [];

  /**
   * Whether a position counts towards the month being valued.
   *
   * A holding bought in July is not part of what you were worth in March. With
   * a year of price history on record, valuing it there drew a portfolio that
   * never existed — the chart reached back to months before the money did.
   *
   * `since` is inclusive, `until` exclusive: bought in March means March
   * already has it, sold in August means August already does not. Selling used
   * to be a row deletion, which took the position out of every month it had
   * ever been held in — one sale rewrote the whole trend back to the purchase.
   *
   * Either bound missing means unbounded on that side, which is what every row
   * looked like before positions had dates at all.
   */
  const heldIn = (holding, target) =>
    (!isMonth(holding.since) || holding.since <= target)
    && (!isMonth(holding.until) || holding.until > target);

  const holdings = allHoldings.filter(h => heldIn(h, month));
  const fuel = data.fuel || [];
  const carEx = data.carEx || [];
  const history = data.history || [];
  const vehicle = data.vehicle || { name: '—', odo: 0, startOdo: 0 };

  // ---- cashflow -----------------------------------------------------------
  const monthIncome = income.filter(r => r.month === month);
  const monthExpenses = expenses.filter(r => r.month === month);
  const incomeTotal = sumBase(monthIncome);
  const expenseTotal = sumBase(monthExpenses);

  /*
   * What a month of living costs, for the runway: the average spend of the
   * last three months that have any, before this one -- a month still in
   * progress would understate it. A ledger with only this month uses it.
   */
  const spendMonths = [];
  for (let back = 1; back <= 12 && spendMonths.length < 3; back += 1) {
    const rows = expenses.filter(r => r.month === shiftMonth(month, -back));
    if (rows.length) spendMonths.push(sumBase(rows));
  }
  if (!spendMonths.length && monthExpenses.length) spendMonths.push(expenseTotal);
  const monthlyOutgoings = spendMonths.length
    ? spendMonths.reduce((total, value) => total + value, 0) / spendMonths.length
    : 0;
  /* The same three months' income, so the simulator knows what is left each month. */
  const earnMonths = [];
  for (let back = 1; back <= 12 && earnMonths.length < 3; back += 1) {
    const rows = income.filter(r => r.month === shiftMonth(month, -back));
    if (rows.length) earnMonths.push(sumBase(rows));
  }
  if (!earnMonths.length && income.some(r => r.month === month)) {
    earnMonths.push(sumBase(income.filter(r => r.month === month)));
  }
  const monthlyIncome = earnMonths.length
    ? earnMonths.reduce((total, value) => total + value, 0) / earnMonths.length
    : 0;
  const saved = incomeTotal - expenseTotal;
  const savingsRate = incomeTotal ? saved / incomeTotal : 0;

  const prevMonth = shiftMonth(month, -1);
  const prevIncome = sumBase(income.filter(r => r.month === prevMonth));
  const incomeDelta = incomeTotal - prevIncome;

  // ---- investments --------------------------------------------------------

  /*
   * What a holding was worth in the month being looked at, not what it is worth
   * now. The stored price is the current one and stays the fallback: a holding
   * with nothing on record is valued at today's figure rather than left out,
   * which is what every account looked like before prices had a history.
   */
  /**
   * What one unit of a holding was worth in a given month.
   *
   * Cash is never priced from the quote history: the amount is the amount.
   *
   * A balance is recorded under its currency code, and those collide with real
   * tickers -- a 204 dollar balance sits under `USD`, and so does whatever
   * instrument the provider calls USD. Refreshing prices before amount-only
   * classes were excluded wrote a quote against that ticker, and the balance
   * has read 84 dollars ever since: a quote standing in for money somebody
   * actually has.
   *
   * Excluding cash from the refresh and the backfill fixed the writing and did
   * nothing for the rows already written. This is the reading, which is the
   * half that matters -- it makes the confusion impossible and puts every
   * affected balance right the moment it loads, with no migration and nothing
   * for anybody to press.
   *
   * One function, called from both valuation paths, for the same reason
   * `withInterest` is: the dashboard and the trend each worked a price out for
   * themselves, and the moment they disagree a bar stops matching the figure
   * you see when you navigate to that month.
   */
  const unitPriceAt = (holding, target) => {
    if (isAmountOnly(holding.cls)) return num(holding.price);

    /* Per ounce as quoted; a holding weighed in grams is worth that over 31.1 -- see metalUnitFactor. */
    const recorded = priceForMonth(data.priceHistory, holding.ticker, target);
    return (recorded === null ? num(holding.price) : recorded) * metalUnitFactor(holding);
  };

  const priceAt = holding => unitPriceAt(holding, month);

  /*
   * What a cash position is actually holding in a given month.
   *
   * A deposit is the one holding whose worth needs no quote: principal, rate
   * and elapsed time say exactly what is in it, so a stated rate is applied
   * rather than left as a note. Compounded annually, which is what a headline
   * rate means, and only forward from the month the money was set aside.
   *
   * Interest is taxed in most places, and this does not know the rate or the
   * regime -- so the field is the rate the account actually pays the holder,
   * and the copy beside it says to enter the net figure.
   *
   * Deliberately one function called from all three valuation sites. The
   * dashboard, the trend bars and the holdings table each work out a value of
   * their own, and the moment two of them disagree a bar stops matching the
   * figure you see when you navigate to that month.
   */
  const withInterest = (holding, unit, target) => {
    if (!isAmountOnly(holding.cls)) return unit;

    const rate = num(holding.yieldPct);
    const months = monthsBetween(holding.since, target);
    if (!(rate > 0) || months <= 0) return unit;

    return unit * Math.pow(1 + rate / 100, months / 12);
  };

  const invValue = holdings.reduce((a, h) => {
    const value = convertAmount(num(h.units) * withInterest(h, priceAt(h), month), h.currency);
    return a + (value === null ? 0 : value);
  }, 0);
  /** Everything owed at the end of a month, at that month's rates. */
  const debtTotalAt = (target, at = ratesAt(target)) => allDebts.reduce((total, debt) => {
    const balance = balanceAt(debt, target);
    if (!balance) return total;
    const value = toBase(balance, debt.currency, base, at);
    return total + (value === null ? 0 : value);
  }, 0);

  /**
   * What the account was worth at the end of a given month.
   *
   * Holdings are valued at that month's prices and converted at that month's
   * rates, and each debt at the balance it had that month. Cash balances have
   * no history of their own, so they count as they stand. It is the same
   * arithmetic the dashboard does for whichever month is on screen — which is
   * the point: a bar has to agree with the figure you see when you navigate to it.
   */
  const netWorthAt = target => {
    const at = ratesAt(target);
    const invested = allHoldings.filter(h => heldIn(h, target)).reduce((a, h) => {
      const unit = unitPriceAt(h, target);
      const value = toBase(num(h.units) * withInterest(h, unit, target), h.currency, base, at);
      return a + (value === null ? 0 : value);
    }, 0);

    const held = cashBalances.reduce((a, b) => {
      const value = toBase(num(b.amount), b.currency, base, at);
      return a + (value === null ? 0 : value);
    }, 0);

    const owed = debtTotalAt(target, at);

    return invested + held - owed;
  };

  const invCost = holdings.reduce((a, h) => {
    const value = convertAmount(num(h.units) * num(h.cost), h.currency);
    return a + (value === null ? 0 : value);
  }, 0);
  const invGain = invValue - invCost;
  const invGainPct = invCost ? invGain / invCost : 0;

  // ---- liabilities --------------------------------------------------------
  const debtTotal = sumBase(debts, 'balance');
  const debtOriginal = sumBase(debts, 'original');
  const debtPayments = sumBase(debts, 'payment');
  const debtPaidPct = debtOriginal ? (debtOriginal - debtTotal) / debtOriginal : null;
  const interestYear = debts.reduce((a, d) => {
    const value = convertAmount(num(d.balance) * num(d.rate) / 100, d.currency);
    return a + (value === null ? 0 : value);
  }, 0);

/*
 * What the account is worth in the month on screen.
 *
 * For a month that has been closed, this is the reading taken at the time.
 * Cash balances and holdings are current figures with no past of their own,
 * so a closed month cannot be reconstructed from them -- the arithmetic below
 * values that month's holdings against today's cash, which is a figure that
 * was never true on any date. The recording is.
 *
 * It also keeps the headline and the trend telling the same story: the bar for
 * the month on screen carries this number, and a month that disagreed with its
 * own highlighted bar was the visible half of this.
 */
  const observedNow = history.find(h => h.month === month);
  const netWorth = (observedNow && month < thisMonth)
    ? num(observedNow.net)
    : invValue + cash - debtTotal;

  // ---- net worth trend ----------------------------------------------------
  /*
   * A fixed run of months ending at the one on screen, rather than "whatever
   * happens to be recorded". The recorded-only version drew a different number
   * of bars depending on which month you were looking at — July showed July and
   * August, August showed August alone — because a month only existed while it
   * was being viewed.
   *
   * A recorded month uses its recorded figure: that is an observation, made at
   * the time. Everything else is worked out from what is on record now, which
   * is exactly what the dashboard shows when you navigate to that month.
   */
  /*
   * The window reaches past today, and does not simply end at whatever month
   * is being viewed.
   *
   * It used to run from eleven months back to the month on screen, which made
   * the viewed month the last bar every time. Two things followed, and both
   * were reported: nothing ahead of today was ever drawn, so the dashed
   * projection the chart explains at the bottom could only appear once you had
   * already navigated into the future; and once the bars became clickable,
   * looking at a past month left a chart with no bar after the one you were on
   * -- a one-way street out of the present.
   *
   * So the window ends `TREND_AHEAD` months after today and keeps its length.
   * It stays put as you click from bar to bar, which is also what makes the
   * chart usable as navigation: the columns do not move under the pointer.
   * Only a month outside it drags it along, and then only far enough to
   * include that month.
   */
  let windowEnd = shiftMonth(thisMonth, TREND_AHEAD);
  if (month > windowEnd) windowEnd = month;
  let windowStart = shiftMonth(windowEnd, -(TREND_MONTHS - 1));
  if (month < windowStart) {
    windowStart = month;
    windowEnd = shiftMonth(windowStart, TREND_MONTHS - 1);
  }

  const series = [];
  for (let step = 0; step < TREND_MONTHS; step++) {
    const stamp = shiftMonth(windowStart, step);
    const recorded = history.find(h => h.month === stamp);
    /*
     * An observation keeps its value whichever month is on screen.
     *
     * The month being viewed used to be special-cased ahead of its own
     * recording, so selecting a bar replaced the reading taken at the time
     * with what today's balances come to: August read -18.5k from September,
     * -17.0k once you clicked it, and -18.5k again as soon as you left. The
     * chart is a function of the ledger, not of where the pointer is -- so the
     * viewed month gets no say here, and only the highlight moves.
     *
     * A month still in progress is the exception, and the reason the cut-off
     * is `thisMonth` rather than "is it recorded": a reading taken part-way
     * through this month is already out of date, and the balances as they
     * stand are the better answer.
     */
    const observed = Boolean(recorded) && stamp < thisMonth;
    series.push({
      month: stamp,
      net: observed ? num(recorded.net) : netWorthAt(stamp),
      recorded: observed
    });
  }

  /*
   * Scaled across the range in view rather than up from zero.
   *
   * Net worth moves by a few per cent a month, so a zero-based column chart
   * draws twelve bars of visibly identical height and hides the very thing it
   * exists to show. The trade is that the baseline is no longer zero, which
   * would be a misleading chart on its own — so the range travels with the
   * bars and the panel states it, and every bar keeps its own figure.
   */
  /*
   * The months in the window before the ledger holds anything read as zero,
   * and zero is not a reading. Scaled with the rest, they set the range: a new
   * account whose first month is a debt drew eight full-height bars of "0"
   * followed by an empty one for the month that actually has figures in it --
   * the chart upside down. So the run of empty months at the start is left
   * out of the range and drawn as nothing at all.
   */
  const firstLive = series.findIndex(point => num(point.net) !== 0);
  const lead = Math.max(0, firstLive);
  const nets = series.slice(lead).map(point => num(point.net));
  const netHigh = Math.max(...nets);
  const netLow = Math.min(...nets);
  const spread = netHigh - netLow;

  /*
   * Where the month on screen sits in the window -- no longer always the end.
   * Clamped rather than trusted: the window is built to contain it, and a -1
   * here would silently compare against the wrong month.
   */
  const viewedAt = Math.max(0, series.findIndex(point => point.month === month));

  const netBars = series.map((point, index) => ({
    month: point.month,
    net: num(point.net),
    /** Before the ledger begins: no bar and no figure. */
    empty: index < lead,
    /*
     * A flat run draws at full height because every month is the high — except
     * when every month is zero, where full-height bars read as a portfolio
     * rather than an empty ledger.
     */
    ratio: index < lead ? 0 : spread > 0 ? (num(point.net) - netLow) / spread : (netHigh === 0 ? 0 : 1),
    /** Whether this month was observed, as opposed to worked out afterwards. */
    recorded: point.recorded,
    // The highlighted bar is the month on screen — the last one in the run.
    current: point.month === month,
    /*
     * A month that has not happened yet, drawn from today's prices.
     *
     * Navigating forward is allowed and useful — it is how you see what a
     * standing debt payment does to next quarter. But a holding's price for a
     * month that has not arrived cannot be known by anyone, so the bar carries
     * the latest price forward. It is the only bar on the chart that is a guess
     * rather than a reading, and until it said so it looked exactly like the
     * eleven measurements beside it.
     *
     * Flagged only when something is actually held then: with no investments,
     * a future bar is cash and debts carried forward, and "the price is not
     * known" would be answering a question nobody asked.
     */
    projected: point.month > thisMonth && allHoldings.some(hold => heldIn(hold, point.month))
  }));

  /** The month immediately before the one being viewed. */
  const prevPoint = viewedAt > 0 ? series[viewedAt - 1] : null;
  const netDelta = prevPoint ? netWorth - num(prevPoint.net) : 0;
  /*
   * There is only something to compare against once the account has a past:
   * a brand-new ledger would otherwise announce "+0 versus a month in which it
   * did not exist".
   */
  const hasPrevNet = Boolean(prevPoint) && viewedAt - 1 >= lead
    && (history.length > 0 || num(prevPoint.net) !== 0);
  /** Its month, so the label names what it actually compared against. */
  const prevNetMonth = prevPoint ? prevPoint.month : null;

  // ---- spend by category --------------------------------------------------
  const catTotals = new Map();
  monthExpenses.forEach(r => {
    const value = convertAmount(r.amount, r.currency);
    if (value !== null) catTotals.set(r.label, (catTotals.get(r.label) || 0) + value);
  });
  const categories = [...catTotals.entries()]
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value);
  /*
   * What each category usually costs: the average of the three months before
   * this one -- of those that have any spending in them, because a month
   * before the ledger began is not a month in which nothing was spent.
   */
  const usualMonths = [1, 2, 3].map(back => shiftMonth(month, -back))
    .filter(stamp => expenses.some(r => r.month === stamp));
  const usualTotals = new Map();
  expenses.forEach(r => {
    if (!usualMonths.includes(r.month)) return;
    const value = convertAmount(r.amount, r.currency);
    if (value !== null) usualTotals.set(r.label, (usualTotals.get(r.label) || 0) + value);
  });
  const usual = key => (usualMonths.length ? (usualTotals.get(key) || 0) / usualMonths.length : null);
  const catAverageTotal = usualMonths.length
    ? [...usualTotals.values()].reduce((sum, value) => sum + value, 0) / usualMonths.length
    : null;

  /* Scaled to the larger of this month and the usual, so both marks fit. */
  const catMax = Math.max(1, ...categories.map(c => Math.max(c.value, usual(c.key) || 0)));
  const catBars = categories.map(c => ({
    key: c.key,
    value: c.value,
    ratio: catMax ? c.value / catMax : 0,
    /** The usual month, or null with no month before this one to go by. */
    average: usual(c.key),
    averageRatio: usual(c.key) === null ? null : usual(c.key) / catMax,
    /* A category of one's own: the neutral grey, apart from Other. */
    color: CATEGORY_COLORS[c.key] || '#8FA3BA'
  }));

  /*
   * Budgets: each category's monthly limit against what this month has spent
   * on it, in the base currency. A limit set in another currency is converted
   * at the month's rate; one with no rate is left out rather than guessed.
   */
  const budgetRows = (data.budgets || [])
    .map(b => {
      const limit = convertAmount(num(b.amount), b.currency);
      if (limit === null || !(limit > 0)) return null;
      const spent = catTotals.get(b.category) || 0;
      return {
        key: b.category, limit, spent, left: limit - spent, ratio: spent / limit, over: spent > limit + 0.005,
        amount: num(b.amount), currency: b.currency,
        color: CATEGORY_COLORS[b.category] || '#8FA3BA'
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.ratio - a.ratio);

  // ---- allocation ---------------------------------------------------------
  const allocTotal = invValue + cash + debtTotal || 1;
  const allocBars = [
    { key: 'investments', value: invValue, color: '#3DD9A0' },
    { key: 'cash', value: cash, color: '#6FE8BC' },
    { key: 'debt', value: debtTotal, color: '#EF4E43' }
  ].map(a => ({ key: a.key, value: a.value, color: a.color, ratio: a.value / allocTotal }));

  const classTotals = new Map();
  holdings.forEach(h => {
    const value = convertAmount(num(h.units) * priceAt(h), h.currency);
    if (value !== null) classTotals.set(h.cls, (classTotals.get(h.cls) || 0) + value);
  });
  const classBars = [...classTotals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([key, value], i) => ({
      key,
      value,
      ratio: invValue ? value / invValue : 0,
      color: CLASS_PALETTE[i % CLASS_PALETTE.length]
    }));

  // ---- payoff queue -------------------------------------------------------
  const queue = debts.slice().sort((a, b) => strategy === 'balance'
    ? num(a.balance) - num(b.balance)
    : num(b.rate) - num(a.rate));
  const debtRows = queue.map((d, i) => {
    const original = num(d.original) || num(d.balance);
    const repaid = Math.max(0, original - num(d.balance));
    const payment = num(d.payment);
    const balance = num(d.balance);
    const rate = num(d.rate);
    const fees = num(d.fees);
    const term = Number.isFinite(d.term) && d.term > 0 ? Math.round(d.term) : null;

    /*
     * A stated term is the second way to describe the same loan, and often the
     * only one somebody has to hand: an agreement names a rate and a number of
     * instalments, not a payment to the grosz. When the payment is missing we
     * derive it from the term rather than reporting "no plan" at a person who
     * described their loan perfectly well.
     */
    const scheduled = payment > 0 ? payment : paymentFor(balance, rate, term);
    const schedule = amortise(balance, rate, scheduled);

    return {
      id: d.id,
      rank: i + 1,
      name: d.name,
      /** A loan, or a card whose balance moves only with payments and corrections. */
      kind: d.kind === 'card' ? 'card' : 'loan',
      /** The day of the month the instalment is due, when given. */
      payDay: d.payDay >= 1 && d.payDay <= 31 ? d.payDay : null,
      balance,
      original,
      rate,
      payment,
      /** What the plan actually is, whether typed in or derived from the term. */
      scheduledPayment: scheduled,
      /** True when the figure above was worked out rather than entered. */
      paymentDerived: payment <= 0 && scheduled > 0,
      term,
      fees,
      startMonth: d.startMonth || null,
      /*
       * Against the month before -- negative when it came down. Null when the
       * debt was not on the books then: a debt taken this month has no change
       * to report, only a balance.
       */
      change: d.prevBalance === null || d.prevBalance === undefined ? null : balance - num(d.prevBalance),
      repaid,
      repaidRatio: original ? repaid / original : 0,
      monthsLeft: schedule.months,
      /** What the borrowing costs from here to the last payment. */
      totalInterest: schedule.interest,
      /*
       * Principal, interest and the one-off costs of borrowing. The fees are
       * part of what the credit cost and part of nothing else, so a total that
       * left them out would understate the only number that matters here.
       */
      totalCost: schedule.months === null ? null : balance + schedule.interest + fees,
      /*
       * False when the payment does not even cover the monthly interest, so
       * the balance grows however long you keep paying. Reported rather than
       * dressed up as a very large number of months.
       */
      coversInterest: schedule.coversInterest,
      monthlyInterest: balance * rate / 100 / 12,
      hot: rate > 10,
      currency: d.currency || base
    };
  });

  // ---- holdings -----------------------------------------------------------
  /** Annual inflation per currency, as entered; absent means unknown. */
  const inflationFor = code => {
    const row = (data.inflation || []).find(
      r => String(r.currency).toUpperCase() === String(code || '').toUpperCase()
    );
    return row ? num(row.rate) : null;
  };

  const holdRows = holdings.map(h => {
    const price = priceAt(h);
    const value = num(h.units) * price;
    const cash = isAmountOnly(h.cls);

    /*
     * Cash is valued against inflation, not against a purchase price.
     *
     * Holding 205 dollars is not "one unit bought at 205": there is no price to
     * beat and no quantity to hold, so a gain computed from an average cost is
     * a number about nothing. What such a position actually loses is purchasing
     * power, and that is what its gain column reports — negative, because that
     * is the direction cash goes.
     *
     * The nominal value is untouched and still counts towards net worth in
     * full: you do still have the 205. Only the gain column changes meaning.
     */
    if (cash) {
      const annual = inflationFor(h.currency || base);
      const months = monthsBetween(h.since, month);

      /*
       * Two forces on the same money, and the row reports the net of them.
       *
       * `principal` is what was set aside; `nominal` is what the account holds
       * now, principal plus whatever a stated rate has earned since. A deposit
       * paying above inflation shows a gain, one paying below shows a smaller
       * loss than a bare balance would, and a balance paying nothing behaves
       * exactly as it did before rates existed.
       */
      const principal = value;
      const nominal = num(h.units) * withInterest(h, price, month);
      const earned = nominal - principal;

      /*
       * No inflation rate on record means no figure, not a zero and not a
       * guess — the same rule a missing exchange rate follows. `gain` stays
       * null so the row can say the rate is missing rather than imply nothing
       * was lost. The interest is still real and still counts: it is in
       * `nominal`, which is what the value column and net worth use.
       */
      const real = annual === null || months <= 0
        ? null
        : nominal / Math.pow(1 + annual / 100, months / 12);

      return {
        id: h.id,
        ticker: h.ticker,
        name: h.name,
        cls: h.cls,
        /** Amount-only: there are no units and no cost basis to show. */
        amountOnly: true,
        units: num(h.units),
        since: h.since || null,
        /*
         * The month it stops counting, carried so an earlier month can say so.
         * Browsing June, a position closed in July looked exactly like an open
         * one -- the only way to find out was to open the dialog or walk
         * forward until the row vanished.
         */
        until: h.until || null,
        /** The day it was bought, when known. Null is the ordinary answer. */
        boughtOn: h.boughtOn || null,
        price,
        cost: num(h.cost),
        /** What the account holds now — the amount set aside plus its interest. */
        value: nominal,
        /** The amount set aside, before any interest. */
        principal,
        /** Interest earned since, at the stated rate. Zero when none is stated. */
        earned,
        /** The stated annual rate, or null when the balance pays nothing. */
        yieldPct: num(h.yieldPct) > 0 ? num(h.yieldPct) : null,
        costBasis: 0,
        /** Annual inflation used, or null when none is on record. */
        inflation: annual,
        /** How long it has been held, in months, from `since` to this month. */
        heldMonths: Math.max(0, months),
        /** What it would buy today, in the money of the month it was set aside. */
        realValue: real,
        gain: real === null ? null : real - principal,
        gainPct: real === null || !principal ? null : (real - principal) / principal,
        currency: h.currency || base
      };
    }

    const cost = num(h.units) * num(h.cost);
    const gain = value - cost;
    return {
      id: h.id,
      ticker: h.ticker,
      name: h.name,
      cls: h.cls,
      amountOnly: false,
      units: num(h.units),
      /** The month this position starts counting towards net worth. */
      since: h.since || null,
      /** The month it stops, or null while still held -- see the cash branch. */
      until: h.until || null,
      /** The day it was bought, when known -- see the cash branch. */
      boughtOn: h.boughtOn || null,
      price,
      cost: num(h.cost),
      value,
      /** Paper or physical metal, and what a physical one is weighed in. */
      metalForm: h.metalForm || null,
      unit: h.unit || null,
      /** Fineness of a physical metal, 0..1; null for pure or anything else. */
      purity: h.purity || null,
      principal: cost,
      earned: 0,
      yieldPct: null,
      costBasis: cost,
      inflation: null,
      gain,
      gainPct: cost ? gain / cost : 0,
      currency: h.currency || base
    };
  });

  // ---- vehicle ------------------------------------------------------------
  /*
   * Consumption by the full-to-full method, the one fuel logbooks use.
   *
   * Filling to the brim puts back exactly what was burnt since the tank was
   * last full, so the litres of a full fill over the distance since the
   * previous full fill are a measurement, not an estimate. A partial fill
   * measures nothing on its own -- the tank was not full at either end of it --
   * so its litres are carried into the next full fill's interval instead.
   * Every fill used to be read as a full one, which made a quick 20-litre
   * top-up look like a car that had suddenly become frugal, and the long fill
   * after it like one that had started guzzling.
   *
   * The tank's capacity is deliberately not an input here. It says how much
   * the tank can hold, not how much was in it; only a full tank at both ends
   * of an interval says that, and that is what the flag records.
   */
  const fills = fuel.slice().sort((a, b) => num(a.odo) - num(b.odo));
  /** Measured intervals, full tank to full tank. */
  const spans = [];
  /** The odometer at the last full tank; null until there has been one. */
  let anchorOdo = null;
  /** Litres put in by partial fills since that full tank. */
  let carried = 0;
  const fuelRows = fills.map(row => {
    const litres = num(row.gal);
    /* Absent means full: every fill logged before the flag existed was read
       as a full one, and still is. */
    const full = row.full !== false && row.full !== 0;
    let economy = null;
    if (full) {
      if (anchorOdo !== null) {
        const km = num(row.odo) - anchorOdo;
        economy = economyFrom(km, carried + litres, unitSystem);
        if (economy) spans.push({ km, litres: carried + litres });
      }
      anchorOdo = num(row.odo);
      carried = 0;
    } else if (anchorOdo !== null) {
      /* Before the first full tank there is no level to count from, so a
         partial fill there cannot be placed in any interval. */
      carried += litres;
    }
    return {
      id: row.id,
      date: row.date,
      odo: toDistance(num(row.odo)),
      volume: toVolume(litres),
      cost: num(row.cost),
      currency: row.currency || base,
      full,
      economy
    };
  });
  /*
   * Null, not zero, when nothing can be measured.
   *
   * Consumption is the distance between two full tanks divided by what went in
   * to fill the second, so the first fill of a car has no reading of its own --
   * there is nothing behind it to measure against. Averaging an empty list gave
   * 0, and the panel printed "0,00 l/100km", which is not "unknown": it is the
   * claim that the car burns no fuel. The alert on the overview repeated it as
   * a sentence -- "averaging 0.00 l/100km" -- about a car that had just been
   * filled up.
   *
   * The same rule the rest of the app follows: a figure that cannot be worked
   * out is absent, never guessed at.
   */
  /*
   * Total fuel over total distance -- not the mean of the per-fill readings.
   *
   * The mean gave a 90 km interval exactly the weight of an 800 km one, and
   * short intervals are the noisy ones: the pump clicks off a litre early or
   * late and a short hop swings by a whole litre per hundred. Two fills of
   * 10 l over 100 km and 42 l over 600 km averaged 8.5 l/100 km; the car had
   * actually burnt 52 l over 700 km, which is 7.4.
   */
  const spanKm = spans.reduce((total, span) => total + span.km, 0);
  const spanLitres = spans.reduce((total, span) => total + span.litres, 0);
  const avgEconomy = spans.length ? economyFrom(spanKm, spanLitres, unitSystem) : null;
  const lastSpan = spans[spans.length - 1] || null;
  const lastEconomy = lastSpan ? economyFrom(lastSpan.km, lastSpan.litres, unitSystem) : null;

  /*
   * Where the capacity does belong: how far a full tank goes at the average
   * measured so far. Absent until both are known -- a range worked out from a
   * guessed consumption would be a guess presented as a distance.
   */
  const tankLitres = num(vehicle.tank) > 0 ? num(vehicle.tank) : null;
  const rangeKm = tankLitres && spanLitres > 0 ? tankLitres * spanKm / spanLitres : null;
  /*
   * The two systems run in opposite directions — more miles per gallon is
   * good, fewer litres per 100 km is good — so "better than average" cannot be
   * a plain `>=` comparison.
   */
  const better = economyHigherIsBetter(unitSystem);
  fuelRows.forEach(r => {
    /* Nothing is above an average that does not exist. */
    r.aboveAverage = r.economy != null && avgEconomy != null
      && (better ? r.economy >= avgEconomy : r.economy <= avgEconomy);
  });

  const fuelCost = sumBase(fuel, 'cost');
  const carCost = sumBase(carEx, 'amount');
  const purchasePrice = convertAmount(num(vehicle.purchasePrice), vehicle.currency) ?? 0;
  /*
   * The first fill-up sets the starting and the current odometer to the same
   * reading, so nothing has been driven yet as far as the ledger knows. Cost per
   * kilometre is then unknown (null), not the whole cost over a made-up 1 km.
   */
  const drivenKm = Math.max(0, num(vehicle.odo) - num(vehicle.startOdo));
  const distance = toDistance(drivenKm);
  /*
   * Running cost per unit stays running cost: fuel and upkeep only.
   *
   * The purchase price is a real cost of ownership and is reported beside it,
   * but folding a 60 000 purchase into cost-per-kilometre would swamp the
   * figure this panel exists to track and make two very different cars look
   * identical. They are two questions, so they get two numbers.
   */
  const costPerDistance = drivenKm > 0 ? (fuelCost + carCost) / distance : null;

  const carRows = carEx.slice()
    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    .map(r => ({
      id: r.id,
      date: r.date,
      type: r.type,
      note: r.note,
      amount: num(r.amount),
      currency: r.currency || base,
      /** The odometer at the service, when it was written down -- in the reader's unit, like a fill-up's. */
      odo: r.odo > 0 ? toDistance(num(r.odo)) : null,
      /** Marked as the oil change the reminder counts from. */
      oilChange: Boolean(r.oilChange),
      color: CAR_TYPE_COLORS[r.type] || '#54697D'
    }));

  // ---- attention ----------------------------------------------------------
  const hottestDebt = debts.slice().sort((a, b) => num(b.rate) - num(a.rate))[0] || null;
  const smallestDebt = debts.slice().sort((a, b) => num(a.balance) - num(b.balance))[0] || null;

  /*
   * Every alert has to be earned by data. A brand-new account has none, and
   * saying "you are $0 short this month, largest line: —" or "the vehicle costs
   * $0.00 per mile across 1 mile" is worse than saying nothing: it reads like
   * the app is broken, at exactly the moment a person is deciding whether to
   * trust it with their money.
   */
  const hasCashflow = incomeTotal > 0 || expenseTotal > 0;
  const hasVehicle = fuel.length > 0 || carEx.length > 0;

  const alerts = [];
  if (hottestDebt) {
    alerts.push({
      kind: 'debt',
      tone: 'danger',
      name: hottestDebt.name,
      rate: num(hottestDebt.rate),
      balance: num(hottestDebt.balance),
      monthlyInterest: num(hottestDebt.balance) * num(hottestDebt.rate) / 100 / 12
    });
  }
  if (hasCashflow && saved > 0) {
    alerts.push({
      kind: 'saved',
      tone: 'good',
      month,
      amount: saved,
      pct: savingsRate,
      monthsToClearSmallest: smallestDebt
        ? Math.max(1, Math.ceil(num(smallestDebt.balance) / Math.max(1, saved)))
        : null
    });
  } else if (hasCashflow && saved < 0) {
    alerts.push({
      kind: 'short',
      tone: 'danger',
      amount: Math.abs(saved),
      topCategory: categories[0] ? categories[0].key : null
    });
  }
  /* The budget furthest past its limit, and how many others are past theirs. */
  const overBudget = budgetRows.filter(row => row.over).sort((a, b) => (b.spent - b.limit) - (a.spent - a.limit));
  if (overBudget.length) {
    alerts.push({
      kind: 'budget',
      tone: 'danger',
      category: overBudget[0].key,
      over: overBudget[0].spent - overBudget[0].limit,
      others: overBudget.length - 1
    });
  }
  if (hasVehicle && costPerDistance !== null) {
    alerts.push({
      kind: 'vehicle',
      tone: 'info',
      costPerDistance,
      total: fuelCost + carCost,
      distance,
      avgEconomy
    });
  }

  return {
    month,
    prevMonth,
    strategy,
    baseCurrency: base,
    /** The rates the month was valued at, for figures kept outside the ledger (the journal). */
    rates,
    /** How long the savings last without income, at the recent pace of spending. */
    runway: {
      monthlyOutgoings,
      monthlyIncome,
      basedOn: spendMonths.length,
      months: monthlyOutgoings > 0 ? cash / monthlyOutgoings : null
    },
    /*
     * Currencies present in the ledger with no rate to the base. Their rows are
     * excluded from every total above, and the client says so rather than
     * showing a number that is quietly short.
     */
    missingRates: [...missingRates].filter(Boolean).sort(),
    cashBalances: cashBalances.map(b => ({
      currency: b.currency,
      amount: num(b.amount),
      base: toBase(num(b.amount), b.currency, base, rates)
    })),
    totals: {
      cash,
      income: incomeTotal,
      expenses: expenseTotal,
      saved,
      savingsRate,
      incomeDelta,
      hasPrevIncome: prevIncome > 0,
      netWorth,
      /** True when netWorth is the figure recorded when a closed month ended, not today's sum of its parts. */
      netRecorded: Boolean(observedNow && month < thisMonth),
      netDelta,
      invValue,
      invCost,
      invGain,
      invGainPct,
      debtTotal,
      debtOriginal,
      debtPayments,
      debtPaidPct,
      /* Owed now against owed at the end of last month; negative is progress. */
      debtChange: debtTotal - debtTotalAt(monthBefore),
      /* False in the first month anything was owed: there is no before to compare with. */
      hasPrevDebt: allDebts.some(debt => debtBalanceAt(debt, monthBefore) !== null),
      prevDebtMonth: monthBefore,
      /* The month on screen is after today, so its balances are projected, not recorded. */
      debtProjected: month > thisMonth,
      interestYear,
      entryCount: income.length + expenses.length + holdings.length + fuel.length + carEx.length,
      firstMonth: history.length ? history.map(h => h.month).sort()[0] : month,
      hasPrevNet,
      prevNetMonth,
      /** The range the bars are drawn across, so the panel can say so. */
      netHigh,
      netLow
    },
    counts: {
      cash: monthIncome.length + monthExpenses.length,
      debt: debts.length,
      invest: holdings.length,
      car: fuel.length + carEx.length
    },
    topCategory: categories[0] || null,
    /** True when the account holds nothing at all — used for the first-run hint. */
    isEmpty: income.length === 0 && expenses.length === 0 && debts.length === 0
      && holdings.length === 0 && fuel.length === 0 && carEx.length === 0
      && cash === 0,
    netBars,
    /*
     * The total owed over the same run of months as the net worth trend, so the
     * two charts line up column for column. The point of it is the slope: the
     * one figure a debt tab exists to show is how fast it is coming down.
     */
    debtBars: (() => {
      const months = series.map(point => point.month);
      const values = months.map(target => debtTotalAt(target));
      const high = Math.max(0, ...values);
      return months.map((target, i) => ({
        month: target,
        total: values[i],
        ratio: high > 0 ? values[i] / high : 0,
        current: target === month,
        projected: target > thisMonth
      }));
    })(),
    catBars,
    budgetRows,
    goalRows: goalRows(data.goals || [], month),
    /** The usual month's spending in total (see catBars), or null. */
    catAverageTotal,
    catAverageMonths: usualMonths.length,
    allocBars,
    classBars,
    debtRows,
    holdRows,
    fuelRows: fuelRows.slice().reverse(),
    carRows,
    incomeRows: monthIncome.map(r => ({
      id: r.id, label: r.label, key: r.key || null,
      amount: num(r.amount), currency: r.currency || base
    })),
    expenseRows: monthExpenses.map(r => ({
      id: r.id,
      label: r.label,
      /*
       * What the row actually was, when that differs from the category.
       *
       * An imported statement files "LIDL 2073" under Groceries and keeps the
       * shop name here. Dropping it -- which this did -- turned a month of
       * imported spending into a column reading "Other, Other, Other", with the
       * one piece of information that identifies each row sitting unread in the
       * database.
       */
      note: r.note || null,
      /* Where a mirrored line came from, so its coded note can be put into words. */
      origin: r.origin || null,
      amount: num(r.amount),
      currency: r.currency || base,
      color: CATEGORY_COLORS[r.label] || '#8FA3BA'
    })),
    unitSystem,
    vehicle: {
      name: vehicle.name,
      odo: toDistance(num(vehicle.odo)),
      startOdo: toDistance(num(vehicle.startOdo)),
      /** Raw metric values, for edit forms that write back to the API. */
      odoKm: num(vehicle.odo),
      startOdoKm: num(vehicle.startOdo),
      distance,
      costPerDistance,
      avgEconomy,
      lastEconomy,
      /** Tank capacity in the reader's volume unit, and raw for edit forms. */
      tank: tankLitres === null ? null : toVolume(tankLitres),
      tankLitres,
      /** Distance on a full tank at the measured average, in the reader's unit. */
      range: rangeKm === null ? null : toDistance(rangeKm),
      vin: vehicle.vin || '',
      /** Distance between oil changes, raw and in the reader's unit; null until one is picked. */
      serviceKm: num(vehicle.serviceKm) > 0 ? num(vehicle.serviceKm) : null,
      serviceInterval: num(vehicle.serviceKm) > 0 ? toDistance(num(vehicle.serviceKm)) : null,
      /*
       * How many fills are on record, so the panel can tell "no readings yet"
       * apart from "nothing here at all". One fill with no consumption beside
       * it reads as a fill that was not saved; saying a second one is needed
       * answers the question the dash was leaving open.
       */
      fills: fills.length,
      /** What it cost to buy, and when — both optional. */
      purchasePrice,
      purchaseDate: vehicle.purchaseDate || null,
      fuelCost,
      carCost,
      /** Running costs since purchase: fuel plus upkeep. */
      total: fuelCost + carCost,
      /** Everything the vehicle has cost, the purchase included. */
      totalOwnership: fuelCost + carCost + purchasePrice
    },
    alerts
  };
}


/**
 * Journal statistics.
 *
 * Kept out of {@link computeMetrics} on purpose: nothing here reaches net
 * worth, and a caller must not be able to fold it in by accident. A prop-firm
 * balance is not money you own; a payout, when one arrives, is entered as
 * income like anything else.
 *
 * `sumLots` and the count mirror the footer of the sheet this replaces. The
 * rest is what a spreadsheet footer cannot do: how often the plan was actually
 * followed, and what that costs.
 */
export function tradeStats(trades, { baseCurrency = null } = {}) {
  const rows = Array.isArray(trades) ? trades : [];
  const empty = {
    count: 0, sumLots: 0, netPl: 0, wins: 0, losses: 0, winRate: null,
    followed: 0, followRate: null, plFollowed: 0, plBroken: 0,
    avgWin: 0, avgLoss: 0, best: 0, worst: 0, currencies: [], mixed: false
  };
  if (!rows.length) return empty;

  /*
   * Every figure here is a plain sum, so every row has to be in the same money.
   *
   * It was summing whatever it was given: a 100 USD win and a 100 PLN win came
   * out as 200 of nothing. There is no exchange rate in this function and there
   * should not be — the journal is outside the ledger and has no rate history
   * of its own — so a mixed set is reported as mixed and the totals are
   * withheld rather than quietly wrong.
   */
  const currencies = [...new Set(
    rows.map(row => String(row.currency || baseCurrency || '').toUpperCase()).filter(Boolean)
  )].sort();
  const mixed = currencies.length > 1;

  if (mixed) {
    /*
     * The counting still works — a win is a win in any currency — so the parts
     * that do not add money up are kept and the ones that do are not.
     */
    const wins = rows.filter(row => num(row.netPl) > 0).length;
    const losses = rows.filter(row => num(row.netPl) < 0).length;
    const followed = rows.filter(row => row.rulesFollowed).length;
    const decided = wins + losses;

    return {
      ...empty,
      count: rows.length,
      sumLots: rows.reduce((a, row) => a + num(row.lots), 0),
      wins,
      losses,
      winRate: decided ? wins / decided : null,
      followed,
      followRate: rows.length ? followed / rows.length : null,
      /*
       * Every figure made of money, withheld the same way.
       *
       * `netPl` and the plan split were already null and the rest were left at
       * zero, which is not the same statement: null says "not knowable from
       * mixed money", zero says "it came to nothing". Nothing renders the last
       * four today, and that is exactly why they were easy to leave wrong — a
       * tile added later would have reported a best trade of nought to anyone
       * trading two currencies.
       */
      netPl: null,
      plFollowed: null,
      plBroken: null,
      avgWin: null,
      avgLoss: null,
      best: null,
      worst: null,
      currencies,
      mixed: true
    };
  }

  let sumLots = 0, netPl = 0, wins = 0, losses = 0, followed = 0;
  let plFollowed = 0, plBroken = 0, winTotal = 0, lossTotal = 0;
  let best = -Infinity, worst = Infinity;

  for (const row of rows) {
    const pl = num(row.netPl);
    sumLots += num(row.lots);
    netPl += pl;

    /*
     * Break-even is neither. Counting a zero as a loss would understate a
     * strategy that scratches trades on purpose, which is a normal thing to do
     * and the opposite of a losing one.
     */
    if (pl > 0) { wins += 1; winTotal += pl; }
    else if (pl < 0) { losses += 1; lossTotal += pl; }

    if (row.rulesFollowed) { followed += 1; plFollowed += pl; }
    else plBroken += pl;

    if (pl > best) best = pl;
    if (pl < worst) worst = pl;
  }

  const decided = wins + losses;
  return {
    count: rows.length,
    sumLots,
    netPl,
    wins,
    losses,
    /** Over decided trades only — see the break-even note above. */
    winRate: decided ? wins / decided : null,
    followed,
    followRate: rows.length ? followed / rows.length : null,
    /*
     * The pair this journal exists for: what the plan made, and what departing
     * from it made. A spreadsheet footer cannot answer that.
     */
    plFollowed,
    plBroken,
    avgWin: wins ? winTotal / wins : 0,
    avgLoss: losses ? lossTotal / losses : 0,
    best: best === -Infinity ? 0 : best,
    worst: worst === Infinity ? 0 : worst,
    /** The one currency every figure above is in. */
    currencies,
    mixed: false
  };
}

/**
 * The journal's result, month by month, for the chart above it.
 *
 * A fixed run of months ending at the current one -- or at the latest trade,
 * if one is dated later -- with the empty months left in. A month with no
 * trades is part of the record too: a chart that skipped it would draw a busy
 * spring and a busy autumn as if they were consecutive.
 *
 * Wins, losses and break-evens are counted exactly as `tradeStats` counts
 * them, month by month. Money is withheld for a journal kept in more than one
 * currency, for the same reason it is there -- decided over the whole journal,
 * not per month, or a dollar month would be drawn beside a zloty one on one
 * scale. The counts stand either way.
 */
export function tradeMonths(trades, { now = null, months = 12, baseCurrency = null } = {}) {
  const rows = (Array.isArray(trades) ? trades : [])
    .filter(row => /^\d{4}-\d{2}/.test(String(row?.date || '')));
  const overall = tradeStats(rows, { baseCurrency });

  const current = now || currentMonth();
  const latest = rows.reduce((max, row) => {
    const month = row.date.slice(0, 7);
    return month > max ? month : max;
  }, '');
  const end = latest > current ? latest : current;

  const series = [];
  for (let step = months - 1; step >= 0; step--) {
    const month = shiftMonth(end, -step);
    const inMonth = rows.filter(row => row.date.slice(0, 7) === month);
    const stats = tradeStats(inMonth, { baseCurrency });
    series.push({
      month,
      count: inMonth.length,
      wins: stats.wins,
      losses: stats.losses,
      winRate: stats.winRate,
      netPl: overall.mixed ? null : stats.netPl
    });
  }

  return { series, mixed: overall.mixed, currencies: overall.currencies };
}

/**
 * What a backtest is judged by, beyond the journal's tiles.
 *
 * A backtest is a strategy replayed on past charts, and the questions it has
 * to answer are about the strategy, not the month: does it pay more than it
 * loses (profit factor), how much does one trade earn on average
 * (expectancy), how deep did it fall before it recovered (maximum drawdown),
 * and how many losses in a row did it take (the streak you have to be able to
 * sit through). The equity curve is the running total, trade by trade, in the
 * order the trades happened on the chart.
 *
 * Money figures need one currency, as in `tradeStats`; a mixed session keeps
 * its counts and withholds the rest.
 */
export function backtestStats(trades) {
  const rows = (Array.isArray(trades) ? trades : []).slice().sort((a, b) =>
    String(a.date).localeCompare(String(b.date))
    || String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
  const currencies = [...new Set(rows.map(row => String(row.currency || '').toUpperCase()).filter(Boolean))];
  const out = {
    count: rows.length, mixed: currencies.length > 1, currency: currencies[0] || null,
    profitFactor: null, expectancy: null, maxDrawdown: null,
    avgWin: null, avgLoss: null, longestLosing: 0, equity: []
  };
  if (!rows.length || out.mixed) return out;

  let equity = 0, peak = 0, maxDrawdown = 0;
  let grossWin = 0, grossLoss = 0, wins = 0, losses = 0, streak = 0, longest = 0;

  for (const row of rows) {
    const pl = num(row.netPl);
    equity += pl;
    out.equity.push(equity);
    peak = Math.max(peak, equity);
    maxDrawdown = Math.max(maxDrawdown, peak - equity);

    if (pl > 0) { grossWin += pl; wins += 1; streak = 0; }
    else if (pl < 0) {
      grossLoss += pl; losses += 1;
      streak += 1; longest = Math.max(longest, streak);
    }
    /* Break-even neither ends a losing streak nor adds to it. */
  }

  /* No losses at all is an unbounded ratio, said as such rather than as a number. */
  out.profitFactor = grossLoss < 0 ? grossWin / -grossLoss : (grossWin > 0 ? Infinity : null);
  out.expectancy = equity / rows.length;
  out.maxDrawdown = maxDrawdown;
  out.avgWin = wins ? grossWin / wins : null;
  out.avgLoss = losses ? grossLoss / losses : null;
  out.longestLosing = longest;
  return out;
}

/**
 * The discipline gap: what a setup earns per trade in backtesting, against
 * what it earns when it is traded for real.
 *
 * A backtest measures the strategy; the journal measures the strategy as
 * traded -- by a person, after a losing morning, with rules sometimes bent.
 * The difference between the two, per trade and in money, is the part of the
 * result that is behaviour rather than edge. Setups are matched by name,
 * ignoring case and surrounding spaces, since that is how they are typed.
 *
 * Two leaks are measured on the live side, because they are the usual
 * culprits: trades that broke the plan, and trades taken straight after a
 * loss (tilt). Each is priced as what a trade of that kind earns less than
 * the rest of the setup's live trades. The after-a-loss mark comes from the
 * whole live journal in chronological order -- a loss on one setup tilts the
 * next trade whatever its setup.
 *
 * Money needs one currency per setup, as everywhere in the journal.
 */
export function disciplineGap(trades, { minBacktest = 5, minLive = 3 } = {}) {
  const rows = Array.isArray(trades) ? trades : [];
  const order = (a, b) => String(a.date).localeCompare(String(b.date))
    || String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
  const mean = list => (list.length ? list.reduce((sum, row) => sum + num(row.netPl), 0) / list.length : null);
  const winRate = list => {
    const wins = list.filter(row => num(row.netPl) > 0).length;
    const decided = wins + list.filter(row => num(row.netPl) < 0).length;
    return decided ? wins / decided : null;
  };

  /* Which live trades came straight after a losing one. */
  const afterLoss = new Set();
  const live = rows.filter(row => !row.backtestId).sort(order);
  live.forEach((row, i) => { if (i > 0 && num(live[i - 1].netPl) < 0) afterLoss.add(row.id); });

  const groups = new Map();
  for (const row of rows) {
    const label = String(row.setup || '').trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (!groups.has(key)) groups.set(key, { label, backtest: [], live: [] });
    groups.get(key)[row.backtestId ? 'backtest' : 'live'].push(row);
  }

  const out = [];
  for (const group of groups.values()) {
    if (!group.backtest.length || !group.live.length) continue;
    const currencies = [...new Set([...group.backtest, ...group.live]
      .map(row => String(row.currency || '').toUpperCase()).filter(Boolean))];
    const base = {
      setup: group.label,
      backtestCount: group.backtest.length,
      liveCount: group.live.length,
      enough: group.backtest.length >= minBacktest && group.live.length >= minLive,
      currency: currencies[0] || null,
      mixed: currencies.length > 1
    };
    if (base.mixed) { out.push(base); continue; }

    const backtestPerTrade = mean(group.backtest);
    const livePerTrade = mean(group.live);
    const broken = group.live.filter(row => !row.rulesFollowed);
    const kept = group.live.filter(row => row.rulesFollowed);
    const tilted = group.live.filter(row => afterLoss.has(row.id));
    const calm = group.live.filter(row => !afterLoss.has(row.id));
    const leak = (bad, good) => (bad.length && good.length ? mean(good) - mean(bad) : null);

    out.push({
      ...base,
      backtestPerTrade,
      livePerTrade,
      gap: livePerTrade - backtestPerTrade,
      backtestWinRate: winRate(group.backtest),
      liveWinRate: winRate(group.live),
      /* What a trade that broke the plan earned less than one that kept it. */
      brokenCount: broken.length,
      brokenCost: leak(broken, kept),
      /* What a trade taken after a loss earned less than the others. */
      tiltCount: tilted.length,
      tiltCost: leak(tilted, calm)
    });
  }
  return out.sort((a, b) => b.liveCount - a.liveCount);
}

/**
 * Where a prop account stands against its rules, from the journal trades
 * taken on it (`trade.propAccountId`).
 *
 * The balance is the account size plus their results. The drawdown floor is
 * the size less the maximum drawdown when it is static; when it trails, it
 * follows the highest balance -- at the end of each finished day ('eod'), or
 * after every trade ('intraday', the closest a journal of closed trades gets)
 * -- and, as most firms have it, stops trailing at the starting balance. The
 * daily limit counts from the start of today: a morning's profit is room to
 * lose before the limit is hit. Each rule is optional; one not set is null.
 * `today` is the reader's own date.
 */
export function propRules(account, trades = [], today = localDay()) {
  const start = Math.max(0, num(account.size));
  const rules = account.rules || {};
  const type = ['static', 'eod', 'intraday'].includes(rules.drawdownType) ? rules.drawdownType : 'eod';
  const own = trades
    .filter(row => row.propAccountId === account.id && !row.backtestId)
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)) || String(a.createdAt || '').localeCompare(String(b.createdAt || '')));

  let running = start;
  let peakIntraday = start;
  const dayEnd = new Map();
  for (const row of own) {
    running += num(row.netPl);
    peakIntraday = Math.max(peakIntraday, running);
    dayEnd.set(row.date, running);
  }
  const balance = running;
  const pnl = balance - start;
  /* Days that have ended: today's close is not in yet. */
  const peakEod = Math.max(start, ...[...dayEnd.entries()].filter(([date]) => date < today).map(([, value]) => value));

  const maxDd = num(rules.maxDrawdown) > 0 ? num(rules.maxDrawdown) : null;
  const peak = type === 'intraday' ? peakIntraday : peakEod;
  const floor = maxDd === null ? null : type === 'static' ? start - maxDd : Math.min(start, peak - maxDd);
  const ddRoom = floor === null ? null : balance - floor;

  const todayPl = own.filter(row => row.date === today).reduce((sum, row) => sum + num(row.netPl), 0);
  const dailyLoss = num(rules.dailyLoss) > 0 ? num(rules.dailyLoss) : null;
  const dailyRoom = dailyLoss === null ? null : dailyLoss + todayPl;

  const target = num(rules.target) > 0 ? num(rules.target) : null;
  const clamp = value => Math.max(0, Math.min(1, value));

  const breached = Boolean((ddRoom !== null && ddRoom <= 0) || (dailyRoom !== null && dailyRoom <= 0));
  return {
    trades: own.length, type, start, balance, pnl,
    floor, ddRoom, ddRatio: maxDd === null ? null : clamp(ddRoom / maxDd), maxDrawdown: maxDd,
    todayPl, dailyLoss, dailyRoom, dailyRatio: dailyLoss === null ? null : clamp(dailyRoom / dailyLoss),
    target, targetRatio: target === null ? null : clamp(pnl / target), reached: target !== null && pnl >= target,
    breached,
    /* Under a fifth of the room left, on either limit. */
    warning: !breached && Boolean((maxDd !== null && ddRoom < maxDd * 0.2) || (dailyLoss !== null && dailyRoom < dailyLoss * 0.2)),
    hasRules: maxDd !== null || dailyLoss !== null || target !== null
  };
}

/**
 * Whether prop trading pays for itself.
 *
 * Every fee, reset and activation against every payout and refund, over all
 * the accounts ever bought: the net result, the return on what was spent,
 * how many evaluations were passed, and what one payout has cost in fees.
 * Money in one currency only, as everywhere in the journal; a mixed set keeps
 * its counts and withholds the sums.
 */
export function propStats(accounts) {
  const list = Array.isArray(accounts) ? accounts : [];
  const currencies = [...new Set(list.map(account => String(account.currency || '').toUpperCase()).filter(Boolean))];
  const events = list.flatMap(account => (account.events || []).map(event => ({ ...event, account })));

  const passed = list.filter(account =>
    account.status === 'funded' || (account.events || []).some(event => event.kind === 'payout')).length;
  const failed = list.filter(account => account.status === 'failed').length;
  const out = {
    accounts: list.length,
    /* Still being traded towards funded: a challenge or its second phase, with no payout yet. */
    active: list.filter(account => (account.status === 'challenge' || account.status === 'phase2')
      && !(account.events || []).some(event => event.kind === 'payout')).length,
    passed,
    failed,
    passRate: passed + failed ? passed / (passed + failed) : null,
    payouts: events.filter(event => event.kind === 'payout').length,
    currency: currencies[0] || null,
    mixed: currencies.length > 1,
    costs: null, returns: null, net: null, roi: null, costPerPayout: null, byFirm: []
  };
  if (out.mixed || !list.length) return out;

  const sum = kinds => events.filter(event => kinds.includes(event.kind)).reduce((total, event) => total + num(event.amount), 0);
  out.costs = sum(PROP_COSTS);
  out.returns = sum(PROP_RETURNS);
  out.net = out.returns - out.costs;
  out.roi = out.costs > 0 ? out.net / out.costs : null;
  out.costPerPayout = out.payouts ? out.costs / out.payouts : null;

  const firms = new Map();
  for (const event of events) {
    const firm = event.account.firm;
    if (!firms.has(firm)) firms.set(firm, { firm, costs: 0, returns: 0 });
    const row = firms.get(firm);
    if (PROP_COSTS.includes(event.kind)) row.costs += num(event.amount);
    else row.returns += num(event.amount);
  }
  out.byFirm = [...firms.values()]
    .map(row => ({ ...row, net: row.returns - row.costs }))
    .sort((a, b) => b.net - a.net);
  return out;
}

/**
 * How much of the savings can go into trading, and how much of it this
 * month's trading has already taken.
 *
 * The cushion is `cushionMonths` of recent spending (see metrics.runway) and
 * is not for trading. What the savings hold above it is the risk capital --
 * money whose loss would hurt but not change how you live. The month's real
 * trades (never backtests) are converted into the base currency at the
 * ledger's rates; a trade in a currency without a rate is left out and said.
 *
 * `prop`: the trading is done on prop firm accounts. The firm's capital is
 * what a losing month takes, not the savings, so there is no cushion to keep
 * and every trade's result is the firm's; what the trader's own money pays is
 * the fees, and what comes back the payouts -- this month's, from
 * `propAccounts`, in the base currency.
 */
export function tradingRunway(metrics, trades, { cushionMonths = 6, month = null, prop = false, propAccounts = [] } = {}) {
  const runway = metrics?.runway || { monthlyOutgoings: 0, months: null, basedOn: 0 };
  const base = metrics?.baseCurrency || DEFAULT_BASE_CURRENCY;
  const rates = metrics?.rates || {};
  const savings = num(metrics?.totals?.cash);
  const cushion = prop ? 0 : runway.monthlyOutgoings * cushionMonths;
  const riskCapital = savings - cushion;

  const target = month || metrics?.month;
  let traded = 0;
  const unconverted = new Set();
  for (const row of Array.isArray(trades) ? trades : []) {
    if (row.backtestId || String(row.date).slice(0, 7) !== target) continue;
    const value = toBase(num(row.netPl), row.currency || base, base, rates);
    if (value === null) unconverted.add(String(row.currency).toUpperCase());
    else traded += value;
  }

  let propSpent = 0;
  let propBack = 0;
  for (const account of prop && Array.isArray(propAccounts) ? propAccounts : []) {
    for (const event of account.events || []) {
      if (String(event.date).slice(0, 7) !== target) continue;
      const value = toBase(num(event.amount), account.currency || base, base, rates);
      if (value === null) unconverted.add(String(account.currency).toUpperCase());
      else if (PROP_RETURNS.includes(event.kind)) propBack += value;
      else propSpent += value;
    }
  }

  const lossShare = !prop && riskCapital > 0 && traded < 0 ? -traded / riskCapital : null;
  let status = 'ok';
  if (prop) status = 'prop';
  else if (!runway.basedOn) status = 'unknown';
  else if (riskCapital <= 0) status = 'no-capital';
  else if (lossShare !== null && lossShare >= 0.5) status = 'warning';

  return {
    savings, cushion, cushionMonths, riskCapital,
    monthlyOutgoings: runway.monthlyOutgoings,
    runwayMonths: runway.months,
    tradedThisMonth: traded,
    lossShare,
    propSpent, propBack,
    unconverted: [...unconverted],
    status,
    currency: base
  };
}

/*
 * PIT-38 rounds the tax base and the tax to whole złoty: under 50 grosz down,
 * 50 grosz and over up.
 */
const wholeZloty = value => (value > 0 ? Math.round(value) : 0);

/**
 * An estimate of PIT-38 for one year, from figures already in złoty.
 *
 * @param trades     journal results converted at the NBP D-1 rate: [{ pln }]
 *                   -- a futures result is income when positive, a cost when
 *                   negative, and belongs with securities in one basket
 * @param pit8c      { income, cost } from the PIT-8C a Polish broker sends
 *                   (form row 1, fields 20/21)
 * @param securities { income, cost } of other sales, entered by hand -- a
 *                   foreign broker's report (row 2, fields 22/23, where the
 *                   journal's trades go too)
 * @param losses     earlier years' losses in that basket: [{ year, amount,
 *                   used, once }] -- up to half of each a year, or with
 *                   `once` up to 5 million złoty in a single year
 * @param crypto     { income, cost, carried }: its own basket; its losses
 *                   never offset the one above, only unused costs carry over
 * @param dividends  { gross, withheld } from abroad, in złoty
 *
 * The same arithmetic the form does, section by section, so every figure can
 * be checked against it. Not tax advice: it counts what it is given.
 */
export function pit38({
  trades = [], pit8c = {}, securities = {}, losses = [], crypto = {}, dividends = {}
} = {}) {
  const pos = value => Math.max(0, num(value));
  const tradeIncome = trades.reduce((sum, row) => sum + Math.max(0, num(row.pln)), 0);
  const tradeCost = trades.reduce((sum, row) => sum + Math.max(0, -num(row.pln)), 0);

  const otherIncome = tradeIncome + pos(securities.income);
  const otherCost = tradeCost + pos(securities.cost);
  const income = pos(pit8c.income) + otherIncome;
  const cost = pos(pit8c.cost) + otherCost;
  const result = income - cost;
  const gain = Math.max(0, result);

  /* Oldest first: a loss not deducted within five years is gone. */
  let room = gain;
  const deductions = losses
    .filter(loss => pos(loss.amount) > 0)
    .sort((a, b) => num(a.year) - num(b.year))
    .map(loss => {
      const left = Math.max(0, pos(loss.amount) - pos(loss.used));
      const cap = loss.once ? Math.min(left, 5000000) : Math.min(left, pos(loss.amount) * 0.5);
      const take = Math.min(cap, room);
      room -= take;
      return { year: loss.year, deducted: take, available: left, remaining: left - take };
    });
  const deducted = deductions.reduce((sum, row) => sum + row.deducted, 0);
  const base = wholeZloty(gain - deducted);
  const tax = wholeZloty(base * 0.19);

  const cryptoResult = pos(crypto.income) - pos(crypto.cost) - pos(crypto.carried);
  const cryptoBase = wholeZloty(Math.max(0, cryptoResult));
  const cryptoTax = wholeZloty(cryptoBase * 0.19);

  /* Tax paid abroad counts up to the Polish 19%, never beyond it. */
  const dividendDue = pos(dividends.gross) * 0.19;
  const dividendCredit = Math.min(pos(dividends.withheld), dividendDue);
  const dividendTax = wholeZloty(dividendDue - dividendCredit);

  return {
    trades: { count: trades.length, income: tradeIncome, cost: tradeCost },
    capital: {
      pit8c: { income: pos(pit8c.income), cost: pos(pit8c.cost) }, other: { income: otherIncome, cost: otherCost },
      income, cost, result, gain, loss: Math.max(0, -result), deductions, deducted, base, tax
    },
    crypto: {
      income: pos(crypto.income), cost: pos(crypto.cost), carried: pos(crypto.carried),
      gain: Math.max(0, cryptoResult), carryForward: Math.max(0, -cryptoResult), base: cryptoBase, tax: cryptoTax
    },
    dividends: { gross: pos(dividends.gross), due: dividendDue, credit: dividendCredit, tax: dividendTax },
    total: tax + cryptoTax + dividendTax
  };
}

/**
 * What a set of trades was, as one string: a conversion made from it holds
 * only while the journal still says the same -- a trade added, changed or
 * removed afterwards makes it stale.
 */
export const tradesFingerprint = rows => (rows || [])
  .map(row => [row.id, row.date, row.netPl, row.currency || ''].join(':'))
  .join('|');

/*
 * US Section 1256 contracts: the roots of the futures and the broad-based index
 * options a journal is most likely to hold. Regulated futures and options on
 * them, whatever the market; index options on SPX, NDX, RUT, VIX and the like.
 *
 * A starting guess, never the decision: the person filing ticks and unticks
 * per instrument. Bare BTC and ETH are left out on purpose -- in a journal they
 * are far more often spot coins than CME futures (whose micro roots, MBT and
 * MET, are in).
 */
export const SECTION_1256_ROOTS = Object.freeze([
  /* Equity index */
  'ES', 'MES', 'NQ', 'MNQ', 'YM', 'MYM', 'RTY', 'M2K', 'EMD', 'NKD', 'VX', 'VXM',
  /* Energy */
  'CL', 'MCL', 'QM', 'NG', 'QG', 'RB', 'HO', 'BZ',
  /* Metals */
  'GC', 'MGC', 'SI', 'SIL', 'HG', 'MHG', 'PL', 'PA',
  /* Rates */
  'ZB', 'ZN', 'ZF', 'ZT', 'UB', 'TN', '10Y', '2YY',
  /* Currency futures */
  '6E', '6J', '6B', '6A', '6C', '6S', '6N', '6M', 'M6E', 'M6A', 'M6B', 'E7', 'J7',
  /* Agriculture and livestock */
  'ZC', 'ZS', 'ZW', 'ZL', 'ZM', 'KE', 'LE', 'HE', 'GF',
  /* CME crypto micros */
  'MBT', 'MET',
  /* Broad-based index options */
  'SPX', 'SPXW', 'XSP', 'NDX', 'NDXP', 'RUT', 'RUTW', 'VIX', 'VIXW', 'DJX', 'OEX', 'XEO'
]);

const MONTH_CODES = 'FGHJKMNQUVXZ';

/**
 * The root a symbol trades under: "CME_MINI:MES1!", "/MES", "MESZ5", "MESZ2025"
 * and "MES" are all MES; "SPXW 241018C05800" is SPXW. Anything not recognised
 * comes back as written (upper-cased), so it still groups with itself.
 */
export function contractRoot(symbol) {
  let text = String(symbol || '').trim().toUpperCase().split(/\s+/)[0] || '';
  if (text.includes(':')) text = text.slice(text.lastIndexOf(':') + 1);
  text = text.replace(/^\//, '').replace(/\d!$/, '');
  if (SECTION_1256_ROOTS.includes(text)) return text;
  const dated = text.match(/^([A-Z0-9]{1,4}?)([A-Z])(\d{1,4})$/);
  if (dated && MONTH_CODES.includes(dated[2]) && SECTION_1256_ROOTS.includes(dated[1])) return dated[1];
  return text;
}

/** Whether a symbol is, as far as its root says, a Section 1256 contract. */
export const isSection1256 = symbol => SECTION_1256_ROOTS.includes(contractRoot(symbol));

/**
 * Savings goals, each in its own currency: how far along, and when it will be
 * there at the monthly amount set.
 *
 * `month` is where the counting starts -- the app passes the calendar month.
 * With a deadline, the monthly amount that would still make it is worked out
 * too, and whether the pace set gets there in time. A goal with nothing going
 * in each month has no date; one already reached is simply done.
 */
export function goalRows(goals, month) {
  return goals.map(goal => {
    const target = Math.max(0, num(goal.target));
    const saved = Math.max(0, num(goal.saved));
    const monthly = Math.max(0, num(goal.monthly));
    const left = Math.max(0, target - saved);
    const done = target > 0 && left <= 0.005;
    const monthsToGo = done ? 0 : monthly > 0 ? Math.ceil(left / monthly - 1e-9) : null;
    /*
     * This month's payment is the first: six payments starting in October make
     * the goal in March, and a March deadline leaves six months, not five.
     */
    const readyMonth = monthsToGo === null ? null : shiftMonth(month, Math.max(0, monthsToGo - 1));
    const deadline = isMonth(goal.deadline) ? goal.deadline : null;
    const monthsLeft = deadline ? monthsBetween(month, deadline) + 1 : null;
    return {
      id: goal.id, name: goal.name, currency: goal.currency, target, saved, monthly, left, deadline,
      ratio: target > 0 ? Math.min(1, saved / target) : 0,
      done, monthsToGo, readyMonth,
      /* What it would take each month to make the deadline; null with none, or none left. */
      needed: deadline && !done && monthsLeft > 0 ? left / monthsLeft : null,
      late: Boolean(deadline && !done && (monthsLeft <= 0 || readyMonth === null || readyMonth > deadline)),
      onTrack: Boolean(deadline && !done && monthsLeft > 0 && readyMonth !== null && readyMonth <= deadline)
    };
  });
}

/**
 * Help with US Form 6781, Part I: Section 1256 contracts, marked to market.
 *
 * Whatever the holding period, the year's net result is 60% long-term and 40%
 * short-term capital gain or loss (lines 8 and 9, carried to Schedule D lines 4
 * and 11). `trades` are the year's real trades already in US dollars
 * (`{ pair, usd }`); `picks` overrides the guess per root (true / false).
 * `other` adds Section 1256 contracts from accounts outside the journal (the
 * 1099-B box 11 aggregate of those accounts) and `mtm` the mark-to-market of
 * positions still open on 31 December, which no journal holds. The loss
 * carryback election (line 6) is a choice for the filer, so it is not made
 * here. Not tax advice: it splits what it is given.
 */
export function form6781({ trades = [], picks = {}, other = 0, mtm = 0 } = {}) {
  const byRoot = new Map();
  for (const row of trades) {
    const root = contractRoot(row.pair);
    if (!root) continue;
    const entry = byRoot.get(root) || { root, count: 0, net: 0 };
    entry.count += 1;
    entry.net += num(row.usd);
    byRoot.set(root, entry);
  }
  const instruments = [...byRoot.values()]
    .map(entry => ({
      ...entry,
      suggested: SECTION_1256_ROOTS.includes(entry.root),
      picked: typeof picks[entry.root] === 'boolean' ? picks[entry.root] : SECTION_1256_ROOTS.includes(entry.root)
    }))
    .sort((a, b) => Number(b.picked) - Number(a.picked) || Math.abs(b.net) - Math.abs(a.net));

  const journalNet = instruments.filter(row => row.picked).reduce((sum, row) => sum + row.net, 0);
  const net = journalNet + num(other) + num(mtm);
  const whole = value => Math.round(value);

  return {
    instruments,
    journalNet,
    other: num(other),
    mtm: num(mtm),
    net,
    /* The form's own lines, in whole dollars as the IRS allows; the halves always add up. */
    line7: whole(net),
    shortTerm: whole(net * 0.4),
    longTerm: whole(net) - whole(net * 0.4)
  };
}

/* The federal brackets a person picks from: the rate their last dollar of ordinary income is taxed at. */
export const US_BRACKETS = Object.freeze([10, 12, 22, 24, 32, 35, 37]);

/*
 * The long-term capital gains rate that goes with a bracket: 0% up to the 12%
 * bracket, 20% in the top one, 15% between. The real thresholds are their own
 * income figures and do not line up exactly (part of the 35% bracket already
 * pays 20%), so this is an estimate, and the view says so.
 */
export const usLongTermRate = bracket => (bracket <= 12 ? 0 : bracket >= 37 ? 20 : 15);

/**
 * The federal tax on a Section 1256 result, and what the 60/40 split saves
 * against the same gain taxed as ordinary short-term income. A state rate, if
 * given, is added on the whole gain. No tax on a loss; NIIT is not included.
 */
export function usTax({ net = 0, bracket = 24, stateRate = 0 } = {}) {
  const gain = Math.max(0, num(net));
  const ltRate = usLongTermRate(bracket);
  const longTerm = gain * 0.6;
  const shortTerm = gain * 0.4;
  const federal = longTerm * ltRate / 100 + shortTerm * bracket / 100;
  const state = gain * Math.max(0, num(stateRate)) / 100;
  const tax = federal + state;
  const allShort = gain * bracket / 100 + state;
  return {
    gain, ltRate, longTerm, shortTerm,
    longTermTax: longTerm * ltRate / 100,
    shortTermTax: shortTerm * bracket / 100,
    federal, state, tax, allShort,
    save: allShort - tax,
    blended: 0.6 * ltRate + 0.4 * bracket
  };
}

/* The 1099-B boxes: A-C short-term, D-F long-term; A and D with the basis reported to the IRS. */
export const FORM_8949_BOXES = Object.freeze(['A', 'B', 'C', 'D', 'E', 'F']);
const SHORT_BOXES = ['A', 'B', 'C'];

/* More than a year between the day bought and the day sold: long-term. The day after the anniversary counts. */
export function heldLongTerm(acquired, sold) {
  if (!isDate(acquired) || !isDate(sold)) return false;
  const [y, m, d] = acquired.split('-').map(Number);
  const anniversary = `${y + 1}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return sold > anniversary;
}

/**
 * Help with US Form 8949 and Schedule D: shares, ETFs, options -- everything
 * that is not a Section 1256 contract.
 *
 * `boxes` are the broker's 1099-B totals per box ({ proceeds, cost,
 * adjustment }, the adjustment being a disallowed wash-sale loss, added back).
 * `rows` are sales not on a 1099-B, each with the dates that decide short or
 * long term. `journalShort` is the year's journal trades that are not Section
 * 1256 contracts, taken as short-term -- a journal of trades is day and swing
 * trading. `f6781` is Form 6781's split, which Schedule D carries on lines 4
 * and 11; `carryover` last year's unused losses. A net loss offsets ordinary
 * income up to 3 000 $ a year (1 500 $ married filing separately) and the rest
 * carries forward, short-term first. Not tax advice.
 */
export function form8949({
  boxes = {}, rows = [], journalShort = 0, f6781 = null, carryover = {}, bracket = 24, stateRate = 0, separate = false
} = {}) {
  const pos = value => Math.max(0, num(value));
  const box = key => {
    const b = boxes[key] || {};
    const proceeds = pos(b.proceeds);
    const cost = pos(b.cost);
    const adjustment = pos(b.adjustment);
    return { key, proceeds, cost, adjustment, gain: proceeds - cost + adjustment };
  };
  const boxRows = FORM_8949_BOXES.map(box);
  const sales = rows
    .filter(row => pos(row.proceeds) > 0 || pos(row.cost) > 0)
    .map(row => {
      const proceeds = pos(row.proceeds);
      const cost = pos(row.cost);
      const adjustment = pos(row.adjustment);
      return { ...row, proceeds, cost, adjustment, gain: proceeds - cost + adjustment, long: heldLongTerm(row.acquired, row.sold) };
    });
  const sum = (list, key) => list.reduce((total, row) => total + row[key], 0);

  const shortBoxes = boxRows.filter(row => SHORT_BOXES.includes(row.key));
  const longBoxes = boxRows.filter(row => !SHORT_BOXES.includes(row.key));
  const shortSales = sales.filter(row => !row.long);
  const longSales = sales.filter(row => row.long);
  const f6781Short = f6781 ? num(f6781.shortTerm) : 0;
  const f6781Long = f6781 ? num(f6781.longTerm) : 0;

  const shortTotal = sum(shortBoxes, 'gain') + sum(shortSales, 'gain') + num(journalShort) + f6781Short - pos(carryover.short);
  const longTotal = sum(longBoxes, 'gain') + sum(longSales, 'gain') + f6781Long - pos(carryover.long);
  const net = shortTotal + longTotal;

  /* Schedule D's netting: a loss on one side eats the gain on the other first. */
  let taxedShort = 0;
  let taxedLong = 0;
  if (net > 0) {
    if (shortTotal >= 0 && longTotal >= 0) { taxedShort = shortTotal; taxedLong = longTotal; }
    else if (shortTotal < 0) taxedLong = net;
    else taxedShort = net;
  }
  const ltRate = usLongTermRate(bracket);
  const federal = taxedShort * bracket / 100 + taxedLong * ltRate / 100;
  const state = Math.max(0, net) * Math.max(0, num(stateRate)) / 100;

  const limit = separate ? 1500 : 3000;
  const lossAllowed = net < 0 ? Math.min(-net, limit) : 0;
  const lossLeft = net < 0 ? -net - lossAllowed : 0;
  /* What carries forward keeps its character, the short-term part used up first. */
  const shortLoss = Math.max(0, -shortTotal);
  const longLoss = Math.max(0, -longTotal);
  const carryShort = net < 0 ? Math.max(0, shortLoss - Math.max(0, longTotal) - lossAllowed) : 0;
  const carryLong = net < 0 ? lossLeft - carryShort : 0;

  return {
    boxes: boxRows, sales, shortSales, longSales,
    journalShort: num(journalShort), f6781Short, f6781Long,
    carryShortIn: pos(carryover.short), carryLongIn: pos(carryover.long),
    shortTotal, longTotal, net, taxedShort, taxedLong, ltRate,
    federal, state, tax: federal + state,
    lossAllowed, lossLimit: limit, carryShort, carryLong,
    /* A loss offsets ordinary income: what that is worth at the bracket. */
    lossSaving: lossAllowed * bracket / 100,
    hasShortLoss: shortLoss > 0, hasLongLoss: longLoss > 0
  };
}

/**
 * Germany: Abgeltungsteuer on the year's capital income, for Anlage KAP.
 *
 * Two pots. Shares are their own: a share loss offsets only share gains and
 * otherwise carries forward. Everything else -- futures and CFDs
 * (Termingeschäfte, whose loss cap was abolished in 2024), dividends, interest
 * -- is one general pot, whose losses may also offset share gains. The saver's
 * allowance (1 000 €, 2 000 € filed jointly) comes off the total; tax is 25%,
 * less foreign tax up to that 25%, plus 5.5% Soli and, for church members,
 * Kirchensteuer -- which is deductible, hence (e - 4q) / (4 + k). Figures in
 * euro. Not tax advice.
 */
export function deTax({
  futures = 0, shareGains = 0, shareLosses = 0, shareCarry = 0,
  dividends = 0, withheld = 0, otherCarry = 0, joint = false, church = 0
} = {}) {
  const pos = value => Math.max(0, num(value));
  const shareNet = pos(shareGains) - pos(shareLosses) - pos(shareCarry);
  const general = pos(dividends) + num(futures) - pos(otherCarry);
  const total = Math.max(0, shareNet) + general;
  const allowance = joint ? 2000 : 1000;
  const allowanceUsed = Math.min(allowance, Math.max(0, total));
  const taxable = Math.max(0, total - allowanceUsed);
  const k = [0.08, 0.09].includes(church) ? church : 0;
  const credit = Math.min(pos(withheld), taxable * 0.25);
  const capitalTax = Math.max(0, k ? (taxable - 4 * credit) / (4 + k) : taxable * 0.25 - credit);
  const soli = capitalTax * 0.055;
  const churchTax = capitalTax * k;
  return {
    shareNet, general, total, allowance, allowanceUsed, taxable, credit,
    capitalTax, soli, churchTax, church: k,
    tax: capitalTax + soli + churchTax,
    shareCarryForward: Math.max(0, -shareNet),
    otherCarryForward: Math.max(0, -total)
  };
}

/* The UK annual exempt amount for capital gains, and the disposal proceeds above which SA108 is needed anyway. */
export const UK_AEA = 3000;
export const UK_REPORT_PROCEEDS = 50000;

/**
 * The UK: Capital Gains Tax for one tax year (6 April to 5 April), for SA108.
 *
 * Gains and losses of the year are netted; losses brought forward from earlier
 * years are used only to bring the net down to the annual exempt amount, so
 * none is wasted below it; what is left is taxed at 18% or 24%. Figures in
 * pounds. Spread betting is outside CGT and outside this. Not tax advice.
 */
export function ukTax({ gains = 0, losses = 0, proceeds = 0, cost = 0, broughtForward = 0, rate = 18 } = {}) {
  const pos = value => Math.max(0, num(value));
  const shareNet = pos(proceeds) - pos(cost);
  const yearGains = pos(gains) + Math.max(0, shareNet);
  const yearLosses = pos(losses) + Math.max(0, -shareNet);
  const net = yearGains - yearLosses;
  const bfUsed = net > UK_AEA ? Math.min(pos(broughtForward), net - UK_AEA) : 0;
  const exempt = Math.min(UK_AEA, Math.max(0, net - bfUsed));
  const taxable = Math.max(0, net - bfUsed - UK_AEA);
  const r = rate === 24 ? 24 : 18;
  return {
    shareNet, gains: yearGains, losses: yearLosses, net, bfUsed, exempt, taxable, rate: r,
    tax: taxable * r / 100,
    carriedForward: pos(broughtForward) - bfUsed + Math.max(0, -net),
    mustReport: yearGains > UK_AEA || pos(proceeds) > UK_REPORT_PROCEEDS
  };
}

/**
 * What if: the next few years of net worth, as things are and with a change.
 *
 * Month by month from today's balances. Investments grow at the assumed
 * return; each debt accrues its interest and takes its scheduled payment,
 * and a paid-off debt's instalment goes to savings from then on. What is
 * left of an average month (recent income less recent spending, which
 * already includes the instalments) lands in cash. The change is an extra
 * payment towards the debt first in the queue, and a monthly amount moved
 * from cash into investments. Once every debt is gone the extra payment has
 * nowhere to go, so it is invested too -- the same money leaving cash every
 * month, now working instead of paying interest.
 *
 * Every month is kept (`monthly`, month 0 being today) for the chart, along
 * with the first month cash dips below zero and, when the change is what
 * takes it there, roughly how much less a month would keep it above zero.
 *
 * A projection, not a forecast: it holds income, spending and the return
 * steady, which real years never do.
 */
export function whatIf({
  cash = 0, investments = 0, debts = [], monthlySaving = 0,
  extraDebt = 0, invest = 0, annualReturn = 0, years = 5
} = {}) {
  const months = Math.max(1, Math.round(years * 12));
  const growth = num(annualReturn) / 100 / 12;

  const run = (extra, contribution) => {
    let money = num(cash);
    let held = num(investments);
    const book = debts.map(d => ({ balance: num(d.balance), rate: num(d.rate), payment: num(d.payment) }));
    let interest = 0;
    let debtFree = book.every(d => d.balance <= 0) ? 0 : null;
    let lowest = { cash: money, month: 0 };
    let short = null;
    const yearly = [];
    const owedNow = () => book.reduce((total, d) => total + Math.max(0, d.balance), 0);
    const monthly = [{ month: 0, netWorth: money + held - owedNow(), cash: money, investments: held, debt: owedNow() }];

    for (let m = 1; m <= months; m += 1) {
      held *= 1 + growth;
      let freed = 0;
      /*
       * The average month already spends every instalment, so whatever part of
       * one is not needed -- all of it once a debt is gone, the unused rest in
       * the month it is cleared -- stays with you as savings.
       */
      for (const d of book) {
        if (d.balance <= 0) { freed += d.payment; continue; }
        const due = d.balance * d.rate / 100 / 12;
        interest += due;
        const paid = Math.min(d.payment, d.balance + due);
        freed += d.payment - paid;
        d.balance = d.balance + due - paid;
      }
      /* The overpayment goes to the first debt still open, in queue order. */
      let over = extra;
      for (const d of book) {
        if (over <= 0) break;
        if (d.balance <= 0) continue;
        const paid = Math.min(over, d.balance);
        d.balance -= paid;
        over -= paid;
      }
      /* What no debt needed of the extra payment is invested. */
      money += num(monthlySaving) + freed - extra - contribution;
      held += contribution + over;

      if (debtFree === null && book.every(d => d.balance <= 0.005)) debtFree = m;
      if (money < lowest.cash) lowest = { cash: money, month: m };
      if (short === null && money < -0.005) short = m;
      const owed = owedNow();
      const point = { month: m, netWorth: money + held - owed, cash: money, investments: held, debt: owed };
      monthly.push(point);
      if (m % 12 === 0 || m === months) yearly.push(point);
    }
    return { monthly, yearly, interest, debtFree, lowest, short, end: monthly[monthly.length - 1] };
  };

  const extra = Math.max(0, num(extraDebt));
  const contribution = Math.max(0, num(invest));
  const baseline = run(0, 0);
  const scenario = run(extra, contribution);

  /*
   * When the change is what runs cash dry, about how much less a month keeps
   * it above zero: the steepest shortfall per month, in steps of 50, checked
   * by running it again (less invested first, then less overpaid). Nothing
   * to suggest when cash runs dry even without the change.
   */
  let trim = null;
  if (scenario.short !== null && baseline.short === null) {
    const steepest = Math.max(...scenario.monthly.filter(p => p.month > 0).map(p => -p.cash / p.month));
    let cut = Math.max(50, Math.ceil(steepest / 50) * 50);
    const total = extra + contribution;
    for (let guard = 0; guard < 400 && cut < total; guard += 1) {
      const lessInvest = Math.min(contribution, cut);
      if (run(extra - (cut - lessInvest), contribution - lessInvest).short === null) break;
      cut += 50;
    }
    trim = Math.min(cut, total);
  }

  return {
    baseline,
    scenario,
    months,
    gain: scenario.end.netWorth - baseline.end.netWorth,
    interestSaved: baseline.interest - scenario.interest,
    trim
  };
}

/**
 * A payoff plan, month by month, for debts in the order they are paid off.
 *
 * Each month every open debt accrues its interest and takes its instalment.
 * With `rollover`, the instalment of a debt already paid -- and whatever part
 * of one was not needed in the month it cleared -- goes to the first debt
 * still open in the queue, together with `extra`, the overpayment. Without
 * it (and with no extra) this is simply "keep paying the instalments".
 *
 * Amounts are in one currency; the caller converts. A debt with no instalment
 * is paid only by what rolls over to it, and may never be.
 *
 * @param debts  [{ id, balance, rate (yearly %), payment }] in queue order
 * @returns { months|null, interest, interest12, totals[], paidAt{id: month}, interestBy{id} }
 *          `months` is null when the plan never clears everything (in 50 years).
 */
export function debtPlan({ debts = [], extra = 0, rollover = true, limit = 600 } = {}) {
  const book = debts.map(d => ({
    id: d.id, balance: Math.max(0, num(d.balance)), rate: num(d.rate) / 1200, payment: Math.max(0, num(d.payment)), interest: 0
  }));
  const paidAt = {};
  for (const d of book) if (d.balance <= 0.005) paidAt[d.id] = 0;
  const totals = [];
  let interest = 0;
  let interest12 = 0;
  let month = 0;
  const open = () => book.some(d => d.balance > 0.005);

  while (month < limit && open()) {
    month += 1;
    let pool = Math.max(0, num(extra));
    for (const d of book) {
      if (d.balance <= 0.005) { if (rollover) pool += d.payment; continue; }
      const due = d.balance * d.rate;
      d.balance += due;
      d.interest += due;
      interest += due;
      const paid = Math.min(d.payment, d.balance);
      d.balance -= paid;
      if (rollover) pool += d.payment - paid;
    }
    for (const d of book) {
      if (pool <= 0) break;
      if (d.balance <= 0.005) continue;
      const paid = Math.min(pool, d.balance);
      d.balance -= paid;
      pool -= paid;
    }
    for (const d of book) if (d.balance <= 0.005 && paidAt[d.id] === undefined) paidAt[d.id] = month;
    totals.push(book.reduce((sum, d) => sum + Math.max(0, d.balance), 0));
    if (month === 12) interest12 = interest;
  }
  if (month < 12) interest12 = interest;

  const interestBy = {};
  for (const d of book) interestBy[d.id] = d.interest;
  return { months: open() ? null : month, interest, interest12, totals, paidAt, interestBy };
}

/**
 * The open debts as they stand in `month`, in the order a payoff plan takes
 * them, in the base currency at that month's rates -- what `debtPlan` runs on.
 *
 * Built from the ledger rather than from a month's metrics, so a plan from
 * today reads the same whichever month is on screen: balances, an instalment
 * worked out from the term, the smallest-first order and the rates all move
 * from month to month. A debt whose currency has no rate is left out rather
 * than counted at par.
 *
 * @returns [{ id, name, balance, rate, payment }]
 */
export function debtQueueAt(data, month, { strategy = 'rate', baseCurrency } = {}) {
  const base = baseCurrency || data.settings?.baseCurrency || DEFAULT_BASE_CURRENCY;
  const rates = data.rateHistory ? resolveRates(data.rateHistory, month) : (data.rates || {});
  return (data.debts || [])
    .map(debt => ({ debt, balance: debtBalanceAt(debt, month) }))
    .filter(({ balance }) => balance !== null && num(balance) > 0)
    .sort((a, b) => (strategy === 'balance' ? num(a.balance) - num(b.balance) : num(b.debt.rate) - num(a.debt.rate)))
    .map(({ debt, balance }) => {
      const rate = num(debt.rate);
      const term = Number.isFinite(debt.term) && debt.term > 0 ? Math.round(debt.term) : null;
      const payment = num(debt.payment) > 0 ? num(debt.payment) : paymentFor(num(balance), rate, term);
      return {
        id: debt.id,
        name: debt.name,
        balance: toBase(num(balance), debt.currency, base, rates),
        rate,
        payment: toBase(payment, debt.currency, base, rates)
      };
    })
    .filter(row => row.balance !== null && row.payment !== null);
}
